import { type StoreState, useChatStore } from '@ai-sdk-tools/store';
import type { ComponentType, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { ChatDisclaimer } from './chat-disclaimer';
import { ChatInputContainer, OutlookConnectPrompt } from './chat-input';
import { Messages, type PreviewMessageProps } from './messages';
import { NovaAlert } from './nova-alert';
import { SuggestedPrompts } from './suggested-prompts';
import { ArtifactPreviewProvider } from './tools/artifact-preview/artifact-preview-provider';
import { BrowserPreviewProvider } from './tools/browser-preview/browser-preview-provider';
import { WtwPhiDisclaimerAlert } from './wtw-phi-disclaimer-alert';
import { isToolHandoffBlocking } from '../../../lib/tool-handoff';
import type { UIMessageWithMetadata } from '../../../lib/types';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import type { DataPartRenderer, ToolOutputRenderer } from '../lib/default-renderers';

export interface ChatContentProps {
  // Pass through all props that Messages component expects
  chatUuid?: string;
  className?: string;
  isLoading?: boolean;
  /**
   * Object mapping tool names to their renderer functions.
   * Keys are tool names (e.g., 'processFiles', 'image_generation').
   * Values are renderer functions that take the part and messageUuid.
   */
  toolRenderers?: Record<string, ToolOutputRenderer>;
  /**
   * Object mapping data part types to their renderer functions.
   * Keys are data part types (e.g., 'data-coverageGapAnalysis').
   * Values are renderer functions that take the part and message.
   */
  dataPartRenderers?: Record<string, DataPartRenderer>;
  /**
   * Custom message renderer component.
   * If provided, this component will be used instead of the default PreviewMessage.
   * Must accept the same props as PreviewMessage.
   */
  messageRenderer?: ComponentType<PreviewMessageProps>;
  /**
   * Controls whether inline citations and sources are rendered.
   * Passed through to Messages component.
   */
  enableSourcesRendering?: boolean;
  // Props for input container
  placeholder?: string;
  toolbar?: ReactNode;
  /**
   * Most embedded Nova surfaces only render ChatContent, so they need a local artifact
   * preview provider. The standalone Nova shell mounts a layout-level provider instead
   * so the preview can push the chat content instead of overlaying messages.
   */
  withArtifactPreviewProvider?: boolean;
  /**
   * Opts a chat surface into the browserUse side-panel provider. Off by
   * default because `BrowserPreviewProvider` inserts an extra flex/overflow
   * wrapper around the messages viewport — a regression for chats that
   * never use browserUse (navigator sidebar, embedded panels in
   * accounts/claims/etc). Nova app-level surfaces that ship the browserUse
   * tool should pass `true`.
   */
  withBrowserPreviewProvider?: boolean;
}

/**
 * Content wrapper component that decides between rendering Messages+Input or Settings panel.
 * Keeps Messages component unchanged and focused on its single responsibility.
 *
 * When settings is open, displays only the ChatSettingsPanel.
 * When settings is closed, displays the Messages component with input and disclaimer.
 */
export function ChatConversationContent({
  placeholder,
  toolbar,
  withArtifactPreviewProvider = true,
  withBrowserPreviewProvider = false,
  ...messagesProps
}: ChatContentProps) {
  const {
    currentChatAppType,
    outlookConnectEnabled,
    outlookNeedsReauth,
    reconnectOutlook,
    canConnectOutlook,
    isWtwColleague,
  } = useChatOverlayContext();
  const [latestMessage, chatStatus] = useChatStore(
    useShallow((state: StoreState<UIMessageWithMetadata>) => [state.messages.at(-1), state.status] as const),
  );
  const [isOutlookSessionAlertDismissed, setIsOutlookSessionAlertDismissed] = useState(false);

  const isOutlookChatWithoutConnect = canConnectOutlook && currentChatAppType === 'OUTLOOK' && !outlookConnectEnabled;
  const hasBlockingToolHandoff = latestMessage
    ? isToolHandoffBlocking(latestMessage, { chatStatus, isLatest: true })
    : false;
  const messagesNode = <Messages {...messagesProps} />;

  useEffect(() => {
    if (!outlookNeedsReauth) {
      setIsOutlookSessionAlertDismissed(false);
    }
  }, [outlookNeedsReauth]);

  return (
    <>
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <WtwPhiDisclaimerAlert isWtwColleague={isWtwColleague} />
        {canConnectOutlook && outlookNeedsReauth && !isOutlookSessionAlertDismissed && (
          <div className="absolute top-4 left-1/2 z-20 -translate-x-1/2">
            <NovaAlert
              variant="warning"
              message="Outlook session expired."
              action={{ label: 'Reconnect', onClick: reconnectOutlook }}
              onDismiss={() => setIsOutlookSessionAlertDismissed(true)}
            />
          </div>
        )}
        {withArtifactPreviewProvider ? (
          <ArtifactPreviewProvider>
            {withBrowserPreviewProvider ? (
              <BrowserPreviewProvider>{messagesNode}</BrowserPreviewProvider>
            ) : (
              messagesNode
            )}
          </ArtifactPreviewProvider>
        ) : withBrowserPreviewProvider ? (
          <BrowserPreviewProvider>{messagesNode}</BrowserPreviewProvider>
        ) : (
          messagesNode
        )}
      </div>
      {isOutlookChatWithoutConnect ? (
        <OutlookConnectPrompt />
      ) : hasBlockingToolHandoff ? null : (
        <ChatInputContainer placeholder={placeholder} toolbar={toolbar} topToolBar={<SuggestedPrompts maxRows={2} />} />
      )}
      <ChatDisclaimer isWtwColleague={isWtwColleague} />
    </>
  );
}
